import { IDockviewPanelProps } from "dockview-react";
import React, { JSX, useCallback, useEffect } from "react";
import { useLocation, Link } from "react-router-dom";
import { addFloatingPanel } from "./utils/DockerviewSettings";
import { api } from "./utils/api";
import { unixTimestampToNewYork } from "./utils/utils";
import { FaPlusCircle } from "react-icons/fa";
import { IEditingEvent } from "./Calendar";
import SafeNumberInput from "./SafeNumberInput";
import { useReadAloud, ReadableItem } from "./utils/useReadAloud";
import { ReadAloudBar } from "./utils/ReadAloudBar";
import { ArticleViewer } from "./ArticleViewer";
import { HighlightedText, Mark } from "./utils/HighlightedText";

// Always-screened accounts; the rest of the Tweets list comes from /event-candidates/tweet-sources
// (any account with an extracted event - tweets with a $CASHTAG are screened from every account)
const DEFAULT_TWEET_SOURCES = [
  "Trade_The_News", "notreload_ai", "MarketNews_Feed", "LiveSquawk", "DeItaone",
  "wallstengine", "OracleNYSE", "FirstSquawk", "faststocknewss",
];

interface EventCandidatesResponse {
  published_at: number;
  content: string;
  content_marks: Mark[];
  url: string;
  events: EventCandidate[];
  tickers: string;
}

interface EventCandidate {
  id: number;
  date: string;
  description: string;
  description_marks: Mark[];
  mark: number;
}

interface IEventCandidatesRequest {
  event_date_start: string;
  event_date_end: string;
  published_at_start: string;
  published_at_end: string;
  source: string;
  page: number;
  has_tickers: boolean;
  tickers_search: string;
  event_description_query: string;
  article_title_query: string;
  table_name: string;
  sorting_field: string;
  market_sources_only: boolean;
  unread_only: boolean;
}

interface IAddingEventProgress {
  key: string; // url of the news item + index of the event
  message: string;
}

const EventCandidates = (props?: any) => {
  const location = useLocation();

  const [candidates, setCandidates] = React.useState<EventCandidatesResponse[]>(
    [],
  );
  const [error, setError] = React.useState<string>("");
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [addingEventProgress, setAddingEventProgress] =
    React.useState<IAddingEventProgress>({ key: "", message: "" });

  const [activeArticleUrl, setActiveArticleUrl] = React.useState<string>("");

  const readAloud = useReadAloud([candidates]);
  const [currentlyReadingEventId, setCurrentlyReadingEventId] = React.useState<number | null>(null);

  const [eventCandidatesRequest, setEventCandidatesRequest] =
    React.useState<IEventCandidatesRequest>({
      event_date_start: "",
      event_date_end: "",
      published_at_start: "",
      published_at_end: "",
      source: "bbg_reuters",
      page: 1,
      has_tickers: true,
      tickers_search: "",
      event_description_query: "",
      article_title_query: "",
      table_name: "",
      sorting_field: "event_date ASC, published_at DESC",
      market_sources_only: true,
      unread_only: true,
    });

  function fetchEventCandidates(
    eventCandidatesRequestParam: IEventCandidatesRequest,
  ) {
    setIsLoading(true);
    setError("");

    api
      .post<EventCandidatesResponse>(
        "/event-candidates/",
        eventCandidatesRequestParam,
      )
      .then((response) => {
        console.log(response.data);
        // @ts-ignore
        setCandidates(response.data);
        setError("");
        setIsLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching event candidates:", error);
        setError("Failed to fetch event candidates.");
        setIsLoading(false);
      });
  }

  const markEventCandidate = useCallback((id: number, newMark: number) => {
    setError("");

    let oldMark = 0;
    for (const candidate of candidates) {
      const event = candidate.events.find((e) => e.id === id);
      if (event) { oldMark = event.mark; break; }
    }

    // optimistic update
    setCandidates((prev) =>
      prev.map((candidate) => ({
        ...candidate,
        events: candidate.events.map((event) =>
          event.id === id ? { ...event, mark: newMark } : event,
        ),
      })),
    );

    api
      .post("/mark-event-candidate", { id, mark: newMark })
      .catch((error) => {
        console.error("Error marking event:", error);
        setError("Failed to mark event.");
        setCandidates((prev) =>
          prev.map((candidate) => ({
            ...candidate,
            events: candidate.events.map((event) =>
              event.id === id ? { ...event, mark: oldMark } : event,
            ),
          })),
        );
      });
  }, [candidates]);

  const handleCtlD = useCallback(() => {
    const firstUnreadArticle = candidates.find((c) =>
      c.events.some((e) => e.mark === 0),
    );
    if (firstUnreadArticle) {
      firstUnreadArticle.events
        .filter((e) => e.mark === 0)
        .forEach((e) => markEventCandidate(e.id, 1));
    }
  }, [candidates, markEventCandidate]);

  const [tweetSources, setTweetSources] = React.useState<string[]>(DEFAULT_TWEET_SOURCES);

  useEffect(() => {
    api
      .get<string[]>("/event-candidates/tweet-sources")
      .then((r) => { if (Array.isArray(r.data) && r.data.length) setTweetSources(r.data); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const keyHandler = (e: KeyboardEvent) => {
      const isHotkeyCombo =
        (e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "d";

      // ignore any other key combos
      if (!isHotkeyCombo) return;

      if (!props || !props?.containerApi) return;

      // if this is not the active panel, ignore
      if (props?.containerApi.activePanel?.id !== props?.api?.id) return;

      // Prevent browser refresh
      e.preventDefault();
      e.stopPropagation();
      (e as any).stopImmediatePropagation?.();

      // Run your action instead
      handleCtlD();
    };

    // capture:true to beat the browser/other handlers to it
    window.addEventListener("keydown", keyHandler, { capture: true });

    return () => {
      window.removeEventListener("keydown", keyHandler, {
        capture: true,
      } as any);
    };
  }, [props?.api?.id, props?.containerApi, handleCtlD]);

  function addMarkClass(mark: number): string {
    switch (mark) {
      case 0:
        return "bg-gray-400";
      case 1:
        return "bg-blue-600";
      case 2:
        return "bg-yellow-600";
      default:
        return "bg-gray-400";
    }
  }

  function addEventToCalendar(
    date: string,
    description: string,
    title: string,
    key: string,
  ) {
    setAddingEventProgress((prev) => ({ key, message: `in-progress` }));

    const eventData: IEditingEvent = {
      id: -1,
      title: title,
      tickers: "",
      date: date,
      description: description,
    };

    api
      .post("/calendar-events", eventData)
      .then(() => {
        // update with success message
        setAddingEventProgress((prev) => ({ key, message: `added` }));
      })
      .catch((error) => {
        console.error(error);

        setAddingEventProgress((prev) => ({ key, message: `error` }));
      })
      .finally(() => {});
  }

  useEffect(() => {
    // load tomorrow
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const month = (tomorrow.getMonth() + 1).toString().padStart(2, "0");
    const day = tomorrow.getDate().toString().padStart(2, "0");
    const year = tomorrow.getFullYear();
    const dateStr = `${year}-${month}-${day}`;

    const oneMonthAgo = new Date();
    oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
    const monthAgo = (oneMonthAgo.getMonth() + 1).toString().padStart(2, "0");
    const dayAgo = oneMonthAgo.getDate().toString().padStart(2, "0");
    const yearAgo = oneMonthAgo.getFullYear();
    const dateStrAgo = `${yearAgo}-${monthAgo}-${dayAgo}`;

    fetchEventCandidates({
      ...eventCandidatesRequest,
      event_date_start: dateStr,
      published_at_start: dateStrAgo,
    });

    setEventCandidatesRequest((prev) => ({
      ...prev,
      event_date_start: dateStr,
      published_at_start: dateStrAgo,
    }));
  }, []);

  function renderEventDate(strDate: string) {
    // format YYYY-MM-DD, if in the future (today + 1) highlight with mark
    const eventDate = new Date(strDate);
    const today = new Date();

    if (eventDate > today) {
      return <strong className="ner-loc">{strDate}</strong>;
    }

    if (eventDate.toDateString() === today.toDateString()) {
      return <strong className="ner-per">{strDate}</strong>;
    }

    return <strong className="whitespace-nowrap">{strDate}</strong>;
  }

  function getRelativeDateText(dateStr: string): string {
    const eventDate = new Date(dateStr);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    eventDate.setHours(0, 0, 0, 0);

    const diffTime = eventDate.getTime() - today.getTime();
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < -1) return `${Math.abs(diffDays)} days ago`;
    if (diffDays === -1) return "Yesterday";
    if (diffDays === 0) return "Today";
    if (diffDays === 1) return "Tomorrow";
    if (diffDays > 1) return `In ${diffDays} days`;
    return "Event no date";
  }

  const readableItems: ReadableItem[] = candidates
    .flatMap((candidate) => {
      const unreadEvents = candidate.events.filter((e) => e.mark === 0);
      return unreadEvents.map((event) => ({
        key: `event-${event.id}`,
        texts: [`${getRelativeDateText(event.date)}: ${event.description}.`],
        onTextStart: () => setCurrentlyReadingEventId(event.id),
        onTextDone: () => markEventCandidate(event.id, 1),
      }));
    });

  const onItemDone = useCallback((_key: string) => {}, []);

  return (
    <div className="relative flex flex-col h-full w-full">
      <div className="flex-1 overflow-auto px-4">
      {location.pathname.startsWith("/mobile") && (
        <Link to="/mobile" className="btn btn-default mt-2 mb-2">To Menu</Link>
      )}
      {error && <p className="text-red-500">Error: {error}</p>}

      <form
        action=""
        className="flex flex-col mt-4 mb-4 gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          fetchEventCandidates(eventCandidatesRequest);
        }}
      >
        <div className="flex flex-col gap-2 md:flex-row">
          <div className="form-group">
            <label
              htmlFor="event-date-start"
              className="cursor-pointer"
              onClick={() => {
                // load tomorrow
                const tomorrow = new Date();
                tomorrow.setDate(tomorrow.getDate() + 1);
                const month = (tomorrow.getMonth() + 1)
                  .toString()
                  .padStart(2, "0");
                const day = tomorrow.getDate().toString().padStart(2, "0");
                const year = tomorrow.getFullYear();
                const dateStr = `${year}-${month}-${day}`;

                const oneMonthAgo = new Date();
                oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
                const monthAgo = (oneMonthAgo.getMonth() + 1)
                  .toString()
                  .padStart(2, "0");
                const dayAgo = oneMonthAgo
                  .getDate()
                  .toString()
                  .padStart(2, "0");
                const yearAgo = oneMonthAgo.getFullYear();
                const dateStrAgo = `${yearAgo}-${monthAgo}-${dayAgo}`;

                // fetch
                fetchEventCandidates({
                  ...eventCandidatesRequest,
                  event_date_start: dateStr,
                  published_at_start: dateStrAgo,
                });

                setEventCandidatesRequest((prev) => ({
                  ...prev,
                  event_date_start: dateStr,
                  published_at_start: dateStrAgo,
                }));
              }}
            >
              Event Start:
            </label>
            <input
              id="event-date-start"
              type="text"
              value={eventCandidatesRequest.event_date_start}
              onChange={(e) =>
                setEventCandidatesRequest((prev) => ({
                  ...prev,
                  event_date_start: e.target.value,
                }))
              }
              placeholder="YYYY-MM-DD"
            />
          </div>

          <div className="form-group">
            <label
              htmlFor="event-date-end"
              className="cursor-pointer"
              onClick={() => {
                // load 1 month in the future
                const futureDate = new Date();
                futureDate.setMonth(futureDate.getMonth() + 1);
                const month = (futureDate.getMonth() + 1)
                  .toString()
                  .padStart(2, "0");
                const day = futureDate.getDate().toString().padStart(2, "0");
                const year = futureDate.getFullYear();
                const dateStr = `${year}-${month}-${day}`;
                setEventCandidatesRequest((prev) => ({
                  ...prev,
                  event_date_end: dateStr,
                }));
              }}
            >
              Event End:
            </label>
            <input
              id="event-date-end"
              type="text"
              value={eventCandidatesRequest.event_date_end}
              onChange={(e) =>
                setEventCandidatesRequest((prev) => ({
                  ...prev,
                  event_date_end: e.target.value,
                }))
              }
              placeholder="YYYY-MM-DD"
            />
          </div>

          <div className="form-group">
            <label htmlFor="published-at-start">Article Start:</label>
            <input
              id="published-at-start"
              type="text"
              value={eventCandidatesRequest.published_at_start}
              onChange={(e) =>
                setEventCandidatesRequest((prev) => ({
                  ...prev,
                  published_at_start: e.target.value,
                }))
              }
              placeholder="YYYY-MM-DD"
            />
          </div>

          <div className="form-group">
            <label htmlFor="published-at-end">Article End:</label>
            <input
              id="published-at-end"
              type="text"
              value={eventCandidatesRequest.published_at_end}
              onChange={(e) =>
                setEventCandidatesRequest((prev) => ({
                  ...prev,
                  published_at_end: e.target.value,
                }))
              }
              placeholder="YYYY-MM-DD"
            />
          </div>

          <div className="form-group w-36">
            <label htmlFor="source">Source:</label>
            <select
              id="source"
              value={eventCandidatesRequest.source}
              onChange={(e) => {
                setEventCandidatesRequest({
                  ...eventCandidatesRequest,
                  source: e.target.value,
                });
              }}
              className="border border-gray-300 rounded p-1 block w-full"
            >
              <option value="bbg_reuters">BBG + Reuters</option>
              <option value="">All</option>

              <optgroup label="Articles">
                <option value="all_articles">All Articles</option>
                <option value="bloomberg">Bloomberg</option>
                <option value="reuters">Reuters</option>
                <option value="marketwatch">MarketWatch</option>
                <option value="barrons">Barron's</option>
                <option value="wsj">WSJ</option>
              </optgroup>

              <optgroup label="Tweets">
                <option value="all_tweets">All Tweets</option>
                {tweetSources.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </optgroup>
            </select>
          </div>

          <div className="form-group w-32">
            <label htmlFor="table-name">Table:</label>

            <select
              id="table-name"
              value={eventCandidatesRequest.table_name}
              onChange={(e) => {
                setEventCandidatesRequest({
                  ...eventCandidatesRequest,
                  table_name: e.target.value,
                });
              }}
              className="border border-gray-300 rounded p-1 block w-full"
            >
              <option value="">All</option>
              <option value="tweets">Tweets</option>
              <option value="articles">Articles</option>
            </select>
          </div>
        </div>

        <div className="flex flex-col gap-2 md:flex-row">
          <div className="form-group">
            <label htmlFor="event-description-query">Event Query:</label>
            <input
              id="event-description-query"
              type="text"
              value={eventCandidatesRequest.event_description_query}
              onChange={(e) =>
                setEventCandidatesRequest((prev) => ({
                  ...prev,
                  event_description_query: e.target.value,
                }))
              }
              placeholder="Event FTS"
            />
          </div>

          <div className="form-group">
            <label htmlFor="article-title-query">Article Query:</label>
            <input
              id="article-title-query"
              type="text"
              value={eventCandidatesRequest.article_title_query}
              onChange={(e) =>
                setEventCandidatesRequest((prev) => ({
                  ...prev,
                  article_title_query: e.target.value,
                }))
              }
              placeholder="Article FTS"
            />
          </div>

          <div className="form-group">
            <label htmlFor="tickers-search">Tickers Search:</label>
            <input
              id="tickers-search"
              type="text"
              value={eventCandidatesRequest.tickers_search}
              onChange={(e) =>
                setEventCandidatesRequest((prev) => ({
                  ...prev,
                  tickers_search: e.target.value,
                }))
              }
              placeholder="Tickers Search"
            />
          </div>

          <div className="form-group w-32">
            <label htmlFor="has-tickers">Has Tickers</label>
            <input
              id="has-tickers"
              type="checkbox"
              checked={eventCandidatesRequest.has_tickers}
              onChange={(e) =>
                setEventCandidatesRequest((prev) => ({
                  ...prev,
                  has_tickers: e.target.checked,
                }))
              }
            />
          </div>

          <div className="form-group w-32">
            <label htmlFor="page">Page:</label>
            <SafeNumberInput
              id="page"
              value={eventCandidatesRequest.page}
              onChange={(value) =>
                setEventCandidatesRequest({
                  ...eventCandidatesRequest,
                  page: value,
                })
              }
              min={1}
              max={10000}
            />
          </div>

          <div className="form-group w-32">
            <label htmlFor="sorting-field">Sort:</label>

            <select
              id="sorting-field"
              value={eventCandidatesRequest.sorting_field}
              onChange={(e) => {
                setEventCandidatesRequest({
                  ...eventCandidatesRequest,
                  sorting_field: e.target.value,
                });
              }}
              className="border border-gray-300 rounded p-1 block w-full"
            >
              <option value="published_at DESC">published_at DESC</option>
              <option value="event_date ASC, published_at DESC">
                event_date ASC, published_at DESC
              </option>
            </select>
          </div>

          <div className="form-group w-48 flex flex-col items-start">
            <label htmlFor="market-sources-only">Market Sources</label>
            <input
              id="market-sources-only"
              type="checkbox"
              checked={eventCandidatesRequest.market_sources_only}
              onChange={(e) =>
                setEventCandidatesRequest((prev) => ({
                  ...prev,
                  market_sources_only: e.target.checked,
                }))
              }
            />
          </div>

          <div className="form-group w-32 flex flex-col items-start">
            <label htmlFor="unread-only">Unread</label>
            <input
              id="unread-only"
              type="checkbox"
              checked={eventCandidatesRequest.unread_only}
              onChange={(e) =>
                setEventCandidatesRequest((prev) => ({
                  ...prev,
                  unread_only: e.target.checked,
                }))
              }
            />
          </div>

          <button type="submit" className="md:hidden btn btn-default">
            Search
          </button>
        </div>
      </form>

      <CandidatesList
        candidates={candidates}
        markEventCandidate={markEventCandidate}
        addEventToCalendar={addEventToCalendar}
        addingEventProgress={addingEventProgress}
        addMarkClass={addMarkClass}
        renderEventDate={renderEventDate}
        isLoading={isLoading}
        readingEventId={currentlyReadingEventId}
        setActiveArticleUrl={setActiveArticleUrl}
      />
      </div>

      {/* ReadAloud bar pinned to bottom */}
      <ReadAloudBar
        voiceControls={readAloud.voiceControls}
        setVoiceControls={readAloud.setVoiceControls}
        toggleReading={readAloud.toggleReading}
        skipItem={readAloud.skipItem}
        items={readableItems}
        onItemDone={onItemDone}
        idPrefix="evt-"
        containerApi={props.containerApi}
        panelApi={props.api}
      />

      {activeArticleUrl !== "" && (
        <ArticleViewer url={activeArticleUrl} setActiveArticleUrl={setActiveArticleUrl} />
      )}
    </div>
  );
};

interface ICandidatesListProps {
  candidates: EventCandidatesResponse[];
  markEventCandidate: (id: number, newMark: number) => void;
  addEventToCalendar: (
    date: string,
    description: string,
    title: string,
    key: string,
  ) => void;
  addingEventProgress: IAddingEventProgress;
  addMarkClass: (mark: number) => string;
  renderEventDate: (strDate: string) => JSX.Element;
  isLoading: boolean;
  readingEventId: number | null;
  setActiveArticleUrl: (url: string) => void;
}

const CandidatesList = ({
  candidates,
  markEventCandidate,
  addEventToCalendar,
  addingEventProgress,
  addMarkClass,
  renderEventDate,
  isLoading,
  readingEventId,
  setActiveArticleUrl,
}: ICandidatesListProps) => {
  function isUsTicker(ticker: string): boolean {
    return ticker.length <= 5 && !ticker.includes(":") && !ticker.includes(".");
  }

  function daysAgo(articleDate: number): string {
    // get the difference in days between now and articleDate
    const articleDateObj = new Date(articleDate * 1000);

    // us timezone is new york
    const now = new Date();
    const utcOffset = -5; // EST
    const estNow = new Date(now.getTime() + utcOffset * 60 * 60 * 1000);

    const diffTime = Math.abs(estNow.getTime() - articleDateObj.getTime());
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    console.log("diffDays:", diffDays);
    return diffDays.toString() + "d";
  }

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <div>
      {candidates.map((candidate, index) => (
        <div
          key={candidate.url}
          id={candidate.url}
          className="flex gap-4 mb-4"
        >
          {/* <div
            className={`min-w-5 ${addMarkClass(Math.min(...candidate.events.map((e) => e.mark)))}`}
          ></div> */}
          <div>
            <ul>
              {candidate.events.map((event, evtIndex) => {
                const isReadingThisEvent = readingEventId === event.id;
                return (
                <div key={evtIndex} id={`event-${event.id}`} className={`mb-1 flex gap-3 ${isReadingThisEvent ? "bg-yellow-100" : ""}`}>
                  <div
                    className={`w-3 self-stretch min-w-3 max-w-3 cursor-pointer ${addMarkClass(event.mark)}`}
                    onClick={() => markEventCandidate(event.id, (event.mark + 1) % 3)}
                  />

                  <div className="space-x-2">
                  <FaPlusCircle
                    onClick={() =>
                      addEventToCalendar(
                        event.date,
                        `${
                          unixTimestampToNewYork(candidate.published_at)[0]
                        }  <br> ${
                          candidate.content
                        } <br> <a target="_blank" href="${
                          candidate.url
                        }">Read more...</a>`,
                        event.description,
                        `${candidate.url}-${evtIndex}`,
                      )
                    }
                    size={22}
                    className="cursor-pointer inline-block"
                  />{" "}

                  {addingEventProgress.key ===
                    `${candidate.url}-${evtIndex}` && (
                    <span className="text-green-600">
                      {addingEventProgress.message}
                    </span>
                  )}{" "}

                  {renderEventDate(event.date)}

                  <span><HighlightedText text={event.description} marks={event.description_marks} /></span>
                  </div>
                </div>
                );
              })}
            </ul>

            <p className="mt-4">
              {unixTimestampToNewYork(candidate.published_at)[0]}{" "}
              {`(${daysAgo(candidate.published_at)} ago)`}
            </p>

            {candidate.tickers && (
              <mark className="bg-orange-400">
                {candidate.tickers.split(" ").map((t, i) => (
                  <React.Fragment key={i}>
                    {i > 0 && " "}
                    {isUsTicker(t) ? <span className="font-semibold">{t}</span> : t}
                  </React.Fragment>
                ))}
              </mark>
            )}

            <br />

            <div className="whitespace-pre-wrap inline">
              <HighlightedText text={candidate.content} marks={candidate.content_marks} />
            </div>
            <br />
            <p className="text-gray-500 gap-4 flex">
              <a href={candidate.url} target="_blank" rel="noopener noreferrer">
                (source)
              </a>
              <a
                href="/"
                onClick={(e) => {
                  e.preventDefault();
                  setActiveArticleUrl(candidate.url);
                }}
              >
                (text)
              </a>
            </p>
          </div>
        </div>
      ))}
    </div>
  );
};

export default EventCandidates;
